import { useEffect, useState } from 'react';
import { Pressable, RefreshControl, ScrollView, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { ActivityIndicator } from 'react-native-paper';
import { useLocalSearchParams, useNavigation, useRouter } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import Toast from 'react-native-toast-message';
import Chip from '@/components/General/Chip';
import Callout from '@/components/General/Callout';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import SolidButton from '@/components/General/SolidButton';
import EnrollmentStepCard from '@/components/FollowUp/Paths/EnrollmentStepCard';
import PathProgressBar from '@/components/FollowUp/Paths/PathProgressBar';
import FollowUpReasonSheet from '@/components/FollowUp/Paths/FollowUpReasonSheet';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { Mutation } from '@/utils/react-query/Mutations';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { canViewModule, isFreeOrg } from '@/utils/organizationEntitlements';
import { extractFollowUpCode } from '@/data/followUp';
import { parseTimeFrame } from '@/utils/dater';
import { getEnrollmentStatusMeta, getStopReasonLabel } from '@/utils/constants/followUp';

/**
 * /follow-up/enrollments/[followUpEnrollmentID] — the flat Path enrollment
 * timeline (§25.5): header (person, Path, status, progress bar, outcome
 * counts), one card per step (current/finished/future), and the secondary
 * authorized actions — reassign, SKIP/RETRY a blocked step, Stop Path.
 * There are no stages, requirements, evidence, approvals, waivers, Pause,
 * or Resume; a later restart is a new enrollment, never a resume.
 */
export default function FollowUpEnrollmentScreen() {
    const router = useRouter();
    const navigation = useNavigation();
    const queryClient = useQueryClient();
    const { followUpEnrollmentID } = useLocalSearchParams();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const { entitlements } = useOrganizationEntitlements();

    const organizationID = loggedInUser?.organizationID;
    const canView = !!loggedInUser?.can?.('View Follow Up');
    const canManage = !!loggedInUser?.can?.('Manage Follow Up');
    const moduleAllowed = canViewModule(entitlements, 'paths');
    const gated = !!loggedInUser && canView && moduleAllowed;

    const [stopOpen, setStopOpen] = useState(false);

    const { data, isLoading, isError, error, isRefetching, refetch } = Query.followUp.enrollment({
        organizationID,
        enrollmentID: followUpEnrollmentID,
        options: { enabled: gated },
    });

    const enrollment = data?.enrollment;
    const steps = data?.steps || [];
    const progress = data?.progress;
    const status = enrollment?.status;
    const meta = getEnrollmentStatusMeta(status);
    const enrollmentActive = status === 'ACTIVE';
    const noContact = enrollment?.stopReason === 'REQUESTED_NO_CONTACT';

    const stopMutation = Mutation.followUp.stopEnrollment({
        onSuccess: (res) => {
            const cancelled = res?.cancelledTaskCount ?? 0;
            Toast.show({
                type: 'success',
                text2: `Path stopped${cancelled ? ` — ${cancelled} open task${cancelled === 1 ? '' : 's'} cancelled` : ''}`,
            });
            setStopOpen(false);
        },
        onError: (mutationError) => {
            if (!mutationError?.expected) {
                Toast.show({ type: 'error', text2: 'That action failed. Please try again.' });
            }
        },
    });

    useEffect(() => {
        const name = enrollment?.personName;
        const path = enrollment?.pathName;
        navigation.setOptions({ title: name && path ? `${name} — ${path}` : name || 'Enrollment' });
    }, [navigation, enrollment?.personName, enrollment?.pathName]);

    const refresh = () => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.enrollment(organizationID, followUpEnrollmentID) });
        queryClient.invalidateQueries({ queryKey: ['followUp', organizationID, 'enrollments'] });
        refetch();
    };

    if (loggedInUser && (!canView || !moduleAllowed)) {
        return (
            <View style={styles.deniedWrap}>
                <EmptyListPlaceholder
                    type="404"
                    spacing={40}
                    message={!moduleAllowed && isFreeOrg(entitlements)
                        ? 'Follow Up is not available on this plan'
                        : "You don't have permission to view Follow Up enrollments."}
                />
            </View>
        );
    }

    if (isError) {
        const code = extractFollowUpCode(error) || error?.followUpCode;
        return (
            <View style={styles.deniedWrap}>
                <EmptyListPlaceholder
                    type="404"
                    spacing={40}
                    message={code === 'FOLLOW_UP_DENIED'
                        ? "You don't have permission to view this enrollment."
                        : code === 'FOLLOW_UP_NOT_FOUND'
                            ? "That enrollment couldn't be found. It may have been removed."
                            : 'Something went wrong loading the enrollment. Pull to refresh to try again.'}
                />
            </View>
        );
    }

    const openWorkCount = steps.filter((s) => ['WAITING', 'ACTIVE', 'NEEDS_ATTENTION'].includes(s?.status)).length;

    const summaryLine = enrollment
        ? [
            `Started ${parseTimeFrame(enrollment.startedAt)}`,
            enrollment.completedAt ? `finished ${parseTimeFrame(enrollment.completedAt)}` : null,
            enrollment.stoppedAt ? `stopped ${parseTimeFrame(enrollment.stoppedAt)}` : null,
        ].filter(Boolean).join(' · ')
        : null;

    return (
        <View style={styles.container}>
            <ScrollView
                style={styles.scroll}
                contentContainerStyle={styles.content}
                keyboardShouldPersistTaps="handled"
                refreshControl={<RefreshControl refreshing={isRefetching} onRefresh={refresh} />}
            >
                {isLoading || !enrollment ? (
                    <View style={styles.loadingWrap}><ActivityIndicator size="large" /></View>
                ) : (
                    <>
                        <View style={styles.header} testID="enrollment-header">
                            <Pressable
                                testID="enrollment-person-link"
                                accessibilityRole="link"
                                onPress={() => router.push(`/people/${enrollment.personID}?tab=followup`)}
                            >
                                <Text style={styles.personName} numberOfLines={1}>
                                    {enrollment.personName || 'Unknown person'}
                                </Text>
                            </Pressable>
                            <Pressable
                                testID="enrollment-path-link"
                                accessibilityRole="link"
                                onPress={() => router.push(`/follow-up/paths/${enrollment.pathID}`)}
                            >
                                <Text style={styles.pathName} numberOfLines={1}>
                                    {enrollment.pathName || 'Path'}
                                </Text>
                            </Pressable>
                            <View style={styles.chipsRow}>
                                <Chip icon={meta.icon} colorVariant={meta.colorVariant}>{meta.label}</Chip>
                                {!!enrollment.stopReason && (
                                    <Chip icon="hand-left-outline" colorVariant="error">
                                        {getStopReasonLabel(enrollment.stopReason)}
                                    </Chip>
                                )}
                            </View>
                            {!!summaryLine && <Text style={styles.metaLine}>{summaryLine}</Text>}
                        </View>

                        {noContact && (
                            <Callout
                                testID="enrollment-no-contact-banner"
                                type="warning"
                                title="Stopped — requested no contact"
                                message={`${enrollment.personName || 'This person'} asked not to be contacted. The Path was stopped${enrollment.stoppedAt ? ` ${parseTimeFrame(enrollment.stoppedAt)}` : ''}.`}
                            />
                        )}
                        {status === 'CANCELLED' && !noContact && (
                            <Callout
                                type="info"
                                message={`This enrollment was stopped${enrollment.stopReason ? ` — ${getStopReasonLabel(enrollment.stopReason)}` : ''}. Its history is kept; a restart is a new enrollment.`}
                            />
                        )}

                        {!!progress && (
                            <PathProgressBar progress={progress} testID="enrollment-progress" />
                        )}

                        {canManage && enrollmentActive && (
                            <View style={styles.actionsRow}>
                                <SolidButton
                                    testID="enrollment-stop"
                                    size="sm"
                                    variant="outlined"
                                    type="error"
                                    iconName="stop-circle-outline"
                                    text="Stop Path"
                                    onPress={() => setStopOpen(true)}
                                />
                            </View>
                        )}

                        <Text style={styles.sectionTitle}>Steps</Text>
                        {steps.length === 0 ? (
                            <Text style={styles.emptyText}>This path has no steps to show.</Text>
                        ) : (
                            steps.map((step, idx) => (
                                <EnrollmentStepCard
                                    key={step.stepProgressID || step.stepID}
                                    organizationID={organizationID}
                                    enrollmentID={enrollment.enrollmentID}
                                    personID={enrollment.personID}
                                    personName={enrollment.personName}
                                    step={step}
                                    index={idx}
                                    enrollmentActive={enrollmentActive}
                                    canManage={canManage}
                                />
                            ))
                        )}
                    </>
                )}
            </ScrollView>

            <FollowUpReasonSheet
                isOpen={stopOpen}
                onClose={() => setStopOpen(false)}
                title="Stop Path"
                subtitle={enrollment?.personName}
                message={`Stopping ends this enrollment.${openWorkCount > 0 ? ` ${openWorkCount} upcoming step${openWorkCount === 1 ? '' : 's'} and any open tasks from this Path will be cancelled.` : ''} Other paths, tasks, and care are unaffected. A later restart is a new enrollment.`}
                reasonLabel="Stop reason"
                reasonRequired={false}
                confirmText="Stop Path"
                loading={stopMutation.isPending}
                testIDPrefix="enrollment-stop-sheet"
                onConfirm={(reason) => {
                    stopMutation.mutate({
                        organizationID,
                        enrollmentID: enrollment.enrollmentID,
                        personID: enrollment.personID,
                        pathID: enrollment.pathID,
                        reason: reason || null,
                    });
                }}
            />
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts }) => ({
    container: { flex: 1 },
    scroll: { flex: 1 },
    content: { padding: 12, paddingBottom: 60, gap: 10, maxWidth: 700, width: '100%', alignSelf: 'center' },
    deniedWrap: { flex: 1, justifyContent: 'center' },
    loadingWrap: { padding: 40, alignItems: 'center' },
    header: { paddingHorizontal: 4, paddingTop: 4, gap: 4 },
    personName: { fontSize: 20, fontFamily: fonts.heading, color: colors.text },
    pathName: { fontSize: 13, fontFamily: fonts.medium, color: colors.paths },
    chipsRow: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginTop: 2 },
    metaLine: { fontSize: 12, color: colors.textSecondary },
    actionsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
    sectionTitle: { fontSize: 13, fontFamily: fonts.heading, color: colors.textSecondary, marginTop: 6 },
    emptyText: { fontSize: 13, color: colors.textSecondary, fontStyle: 'italic', paddingHorizontal: 4 },
}));
